import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { RootState } from '../redux/store';
import { createPatient, CreatePatientPayload } from '../services/api';
import { setPatientRequestId } from '../redux/slices/appointmentSlice';
import { StepCard } from '../components/StepCard';
import { Button } from '../components/Button';
import { PatientTypeEnum, PATIENT_TYPE_TO_BACKEND } from '../types';
import { NewPatientForm } from '../components/patient-details/NewPatientForm';
import { ExistingPatientForm } from '../components/patient-details/ExistingPatientForm';
import { isValidEmail, PHONE_REGEX, MIN_DOB, getYesterday } from '../utils/helpers';


export const StepPatientDetails = () => {
  const { patientType, patientDetails } = useSelector((s: RootState) => s.appointment);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const proceed = () => {
    navigate(`/bookonline/confirmation`);
  };

  const canContinueNew = Boolean(
    patientDetails.firstName &&
    patientDetails.lastName &&
    PHONE_REGEX.test(patientDetails.phone) &&
    patientDetails.email &&
    isValidEmail(patientDetails.email) &&
    patientDetails.birth_date &&
    patientDetails.birth_date <= getYesterday() &&
    patientDetails.birth_date >= MIN_DOB
  );

  const canContinueExisting = Boolean(
    patientDetails.id &&
    PHONE_REGEX.test(patientDetails.phone) &&
    patientDetails.birth_date &&
    patientDetails.birth_date <= getYesterday() &&
    patientDetails.birth_date >= MIN_DOB
  );

  const isExistingPatient = patientType === PatientTypeEnum.Existing;
  const canContinue = isExistingPatient ? canContinueExisting : canContinueNew;

  const savePatient = async () => {
    if (!PHONE_REGEX.test(patientDetails.phone) || !patientDetails.birth_date || patientDetails.birth_date > getYesterday() || patientDetails.birth_date < MIN_DOB || (patientDetails.email && !isValidEmail(patientDetails.email))) {
      return;
    }
    try {
    const backendPatientType =
      patientType && PATIENT_TYPE_TO_BACKEND[patientType as PatientTypeEnum]
        ? PATIENT_TYPE_TO_BACKEND[patientType as PatientTypeEnum]
        : PATIENT_TYPE_TO_BACKEND[PatientTypeEnum.New];
    const payload: CreatePatientPayload = {
      first_name: patientDetails.firstName,
      last_name: patientDetails.lastName,
      email: patientDetails.email,
      phone_number: patientDetails.phone,
      birth_date: patientDetails.birth_date,
      patient_type: backendPatientType,
    };

    if (patientDetails.id) {
      payload.patient_id = patientDetails.id;
    }
    if (patientDetails.external_resource_id) {
      payload.external_resource_id = patientDetails.external_resource_id;
    }
    if (patientDetails.notes) {
      payload.notes = patientDetails.notes;
    }

      const patient_request = await createPatient(payload);
      dispatch(setPatientRequestId(patient_request.request_id));
      proceed();
    } catch (error) {
      console.error('Error creating patient:', error);
    }
  };

  return (
    <Layout>
      <div className="grid grid-cols-1 md:grid-cols-1">
        <aside className="hidden md:block" />
        <section className="md:col-span-2 space-y-4">
          <div>
            <h1 className="text-2xl font-semibold text-black">Patient details</h1>
            <p className="text-sm text-gray-500">Provide your contact information for appointment confirmation.</p>
          </div>
          <StepCard className='overflow-y-auto'>
            {isExistingPatient ? <ExistingPatientForm /> : <NewPatientForm />}
          </StepCard>
          <div className="flex justify-between">
            <Button title='Back' variant='secondary' onClick={() => navigate('/bookonline/step-3')}/>
            <Button title='Continue' disabled={!canContinue} onClick={savePatient}/>
          </div>
        </section>
      </div>
    </Layout>
  );
};